import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Plane, Star, TriangleAlert } from "lucide-react";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import type { CountsValidatedSummaryBo } from "@/types/api/api-types";
import { CountryCombobox } from "@/components/ui/customs/country-combobox";
import { FilterV2, AdditionalFilterV2, useFilterV2 } from "@/components/shared/filter-v2";
import { QuickFilterButton } from "@/components/shared/quickFilterButton";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { useGetCustoms } from "@/api/customs.api";
import { useGetUsersBo } from "@/api/user.api";
import { CustomsCombobox } from "@/components/ui/customs/customs-combobox";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import { ExpeditionBlockedReasonEnum } from "@/constants/enums/expeditionBlockedReason.enum";
import { DateRangePicker } from "@/components/ui/customs/date-range-picker";
import { COUNTRY_REGIONS, REGION_LABELS, type RegionCode } from "@/constants/countries";
import { RolesEnum } from "@/constants/enums/roles.enum";
import type {
  ValidatedExpeditionsFilters as FiltersState,
  useValidatedExpeditionsFilters,
  DeliveryStatusGroup,
} from "./useValidatedExpeditionsFilters.reducer";
import {
  useExpeditionBlockedReasonOptions,
  useManualTrackingOptions,
  usePackagingFilterOptions,
} from "@/hooks/useEnumOptions";

const regionOptions = (Object.keys(COUNTRY_REGIONS) as RegionCode[]).map((code) => ({
  label: REGION_LABELS[code],
  value: code,
}));

const FINALIZED_BY_ROLES = [RolesEnum.ADMIN, RolesEnum.MEMBER];

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function getFinalizedDateLabel(filters: FiltersState): string | null {
  if (!filters.finalizedAt) return null;
  if (filters.finalizedDateFilterType === "DAY") {
    return filters.finalizedAt[0].toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
  if (filters.finalizedDateFilterType === "MONTH") {
    return capitalize(
      filters.finalizedAt[0].toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
    );
  }
  if (filters.finalizedDateFilterType === "RANGE") {
    const dateFormat: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    };
    const [start, end] = filters.finalizedAt;
    return `${start.toLocaleDateString("fr-FR", dateFormat)} - ${end.toLocaleDateString("fr-FR", dateFormat)}`;
  }
  return null;
}

type ValidatedExpeditionsFiltersProps = {
  group: DeliveryStatusGroup;
  filters: FiltersState;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
  counts?: CountsValidatedSummaryBo["quickFilters"];
  showShippingLate?: boolean;
};

export const ValidatedExpeditionsFilters = ({
  group,
  filters,
  actions,
  counts,
  showShippingLate,
}: ValidatedExpeditionsFiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const manualTrackingOptions = useManualTrackingOptions();
  const { isOpen, toggle } = useFilterV2();
  const { data: customs } = useGetCustoms();

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full flex-col items-start justify-between gap-1 xl:flex-row">
        <div className="flex flex-1">
          <ValidatedExpeditionsQuickFilters
            filters={filters}
            actions={actions}
            counts={counts}
            showShippingLate={showShippingLate ?? group === "ongoing"}
          />
        </div>
        <FilterV2
          isOpen={isOpen}
          onToggle={toggle}
          onReset={actions.resetAdvancedFilters}
          hasAdditionalFilters
          className="w-fit self-end"
        >
          <div className="flex w-fit flex-wrap items-center gap-2">
            <div className="w-44">
              <SelectV2
                placeholder={t("validatedFilters.regionPlaceholder")}
                clearable
                options={regionOptions}
                onChange={(e) => actions.setRegion((e as RegionCode) ?? null)}
                value={filters.region ?? undefined}
                searchable={false}
              />
            </div>
            <div className="max-w-xs min-w-[200px] flex-1">
              <SelectV2
                placeholder={t("validatedFilters.trackingPlaceholder")}
                clearable
                options={manualTrackingOptions}
                onChange={(e) => actions.setManualTracking(e)}
                value={filters.manualTracking ?? undefined}
              />
            </div>
            <div className="w-60">
              <CustomsCombobox
                customs={customs ?? []}
                value={filters.customId ?? undefined}
                onValueChange={actions.setCustomId}
                clearable
              />
            </div>
          </div>
        </FilterV2>
      </div>
      <AdditionalFilterV2 isOpen={isOpen}>
        <ValidatedExpeditionsSecondaryContent group={group} filters={filters} actions={actions} />
      </AdditionalFilterV2>
    </div>
  );
};

type QuickFiltersProps = {
  filters: FiltersState;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
  counts?: CountsValidatedSummaryBo["quickFilters"];
  showShippingLate?: boolean;
};

const ValidatedExpeditionsQuickFilters = ({
  filters,
  actions,
  counts,
  showShippingLate,
}: QuickFiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const finalizedDateLabel = getFinalizedDateLabel(filters);

  return (
    <div className="flex flex-wrap items-center gap-4 pt-2">
      {showShippingLate && (
        <QuickFilterButton
          icon={<TriangleAlert className="size-4" />}
          label={t("validatedFilters.shippingLate")}
          count={counts?.shippingLate}
          active={filters.isShippingLate}
          onClick={actions.toggleShippingLate}
          colorClass="text-red-600"
          activeBg="border-red-600 bg-red-600/10"
        />
      )}
      <QuickFilterButton
        icon={<ExpressFullIcon className="size-5" />}
        label={t("validatedFilters.express")}
        count={counts?.express}
        active={filters.isExpress}
        onClick={actions.toggleExpress}
        colorClass="text-[#3562f8]"
        activeBg="border-[#3562f8] bg-[#3562f8]/10"
      />
      <QuickFilterButton
        icon={<Plane className="size-4" fill="currentColor" stroke="none" />}
        label={t("validatedFilters.remote")}
        count={counts?.remote}
        active={filters.isRemote}
        onClick={actions.toggleRemote}
        colorClass="text-foreground"
        activeBg="border-foreground bg-gray-100"
      />
      {finalizedDateLabel && (
        <QuickFilterButton
          icon={<Calendar className="size-4" />}
          label={finalizedDateLabel}
          active
          onClick={() => actions.setFinalizedDay(null)}
          colorClass="text-gray-500"
          activeBg="border-blue-600 bg-blue-50/50 text-blue-700"
          showClose
        />
      )}
      <QuickFilterButton
        icon={<Star className="size-4" />}
        label={t("validatedFilters.customQuotation")}
        count={counts?.customQuotation}
        active={filters.isCustomQuotation}
        onClick={actions.toggleCustomQuotation}
        colorClass="text-purple-500"
        activeBg="border-purple-500 bg-purple-100"
      />

      {/* <div className="bg-border mx-1 hidden h-4 w-px xl:block" aria-hidden />
    <QuickFilterButton
      icon={<UserRound className="size-4" />}
      label="Mes expéditions"
      count={counts?.finalizedBySelf}
      active={filters.isFinalizedBySelf}
      onClick={actions.toggleFinalizedBySelf}
      colorClass="text-gray-700"
      activeBg="border-gray-700 bg-gray-100"
    /> */}
    </div>
  );
};

type SecondaryContentProps = {
  group: DeliveryStatusGroup;
  filters: FiltersState;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
};

const ValidatedExpeditionsSecondaryContent = ({
  group,
  filters,
  actions,
}: SecondaryContentProps) => {
  switch (group) {
    case "blocked":
      return <BlockedSecondaryContent filters={filters} actions={actions} />;
    default:
      return <OngoingDeliveredFailedSecondaryContent filters={filters} actions={actions} />;
  }
};

type SharedProps = {
  filters: FiltersState;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
};

const FinalizedByFilter = ({ filters, actions }: SharedProps) => {
  const { t } = useTranslation("boExpeditions");
  const { data: boUsers } = useGetUsersBo({ role: FINALIZED_BY_ROLES, isDeleted: false });
  const options = useMemo(
    () =>
      (boUsers ?? []).map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      })),
    [boUsers],
  );

  return (
    <div className="max-w-xs min-w-[200px] flex-1">
      <SelectV2
        placeholder={t("validatedFilters.finalizedByPlaceholder")}
        clearable
        options={options}
        onChange={(e) => actions.setFinalizedById(e || null)}
        value={filters.finalizedById ?? undefined}
      />
    </div>
  );
};

const OngoingDeliveredFailedSecondaryContent = ({ filters, actions }: SharedProps) => {
  const { t } = useTranslation("boExpeditions");
  const packagingOptions = usePackagingFilterOptions();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="max-w-xs min-w-[200px] flex-1">
        <CountryCombobox
          value={filters.country ?? undefined}
          onValueChange={actions.setCountry}
          placeholder={t("validatedFilters.countryPlaceholder")}
          clearable
          allowedCodes={filters.region ? COUNTRY_REGIONS[filters.region] : undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <SelectV2
          placeholder={t("validatedFilters.packagingPlaceholder")}
          clearable
          options={packagingOptions}
          onChange={(e) => actions.setPackagingType((e as PackagingFilterEnum) ?? null)}
          searchable={false}
          value={filters.packagingType ?? undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <DateRangePicker
          placeholder={t("validatedFilters.pickupDatePlaceholder")}
          value={filters.pickupDate}
          onChange={actions.setPickupDateRange}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <DateRangePicker
          placeholder={t("validatedFilters.finalizedAtPlaceholder")}
          value={filters.finalizedAt}
          onChange={actions.setFinalizedAtRange}
        />
      </div>
      <FinalizedByFilter filters={filters} actions={actions} />
    </div>
  );
};

const BlockedSecondaryContent = ({ filters, actions }: SharedProps) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const blockedManualOptions = useMemo(
    () => [
      { label: t("common:boolean.yes"), value: "true" },
      { label: t("common:boolean.no"), value: "false" },
    ],
    [t],
  );
  const packagingOptions = usePackagingFilterOptions();
  const blockedReasonOptions = useExpeditionBlockedReasonOptions();
  const blockedManualValue =
    filters.blockedManual === true ? "true" : filters.blockedManual === false ? "false" : undefined;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="max-w-xs min-w-[200px] flex-1">
        <CountryCombobox
          value={filters.country ?? undefined}
          onValueChange={actions.setCountry}
          placeholder={t("validatedFilters.countryPlaceholder")}
          clearable
          allowedCodes={filters.region ? COUNTRY_REGIONS[filters.region] : undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <SelectV2
          placeholder={t("validatedFilters.packagingPlaceholder")}
          clearable
          options={packagingOptions}
          onChange={(e) => actions.setPackagingType((e as PackagingFilterEnum) ?? null)}
          searchable={false}
          value={filters.packagingType ?? undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <SelectV2
          placeholder={t("validatedFilters.blockedManualPlaceholder")}
          clearable
          options={blockedManualOptions}
          onChange={(e) => {
            if (e === "true") actions.setBlockedManual(true);
            else if (e === "false") actions.setBlockedManual(false);
            else actions.setBlockedManual(null);
          }}
          searchable={false}
          value={blockedManualValue}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <SelectV2
          placeholder={t("validatedFilters.blockedReasonPlaceholder")}
          clearable
          options={blockedReasonOptions}
          onChange={(e) => actions.setBlockedReason((e as ExpeditionBlockedReasonEnum) ?? null)}
          value={filters.blockedReason ?? undefined}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <DateRangePicker
          placeholder={t("validatedFilters.pickupDatePlaceholder")}
          value={filters.pickupDate}
          onChange={actions.setPickupDateRange}
        />
      </div>
      <div className="max-w-xs min-w-[200px] flex-1">
        <DateRangePicker
          placeholder={t("validatedFilters.finalizedAtPlaceholder")}
          value={filters.finalizedAt}
          onChange={actions.setFinalizedAtRange}
        />
      </div>
      <FinalizedByFilter filters={filters} actions={actions} />
    </div>
  );
};
